/**
 * A seção de sincronização da ficha com o ERP (história 5.5, AD-8).
 *
 * Ela mora **abaixo** do formulário da ficha, e só na tela de edição: o que ela mostra é a relação
 * de uma Empresa que já existe com o Carga Máquina.
 *
 * **Desde a 13.11c ela aparece em toda ficha carregada**, com a situação em uma frase sem selo:
 * "Cadastrada no Carga Máquina." quando há `erp_pessoa_id`, e "Ainda não está no Carga Máquina."
 * quando não há (FR-27 emendado, decisão de 22/10/2026). Até a 13.11b ela só aparecia "quando tinha
 * o que dizer", porque a ficha que nunca ia ao ERP não tinha selo; com o estado fora do sistema, a pergunta "esta
 * empresa está no Carga Máquina?" sempre tem resposta, e é ela que a vendedora procura. A frase segue
 * **só o vínculo** — a ficha vinculada com uma atualização pendente continua cadastrada lá.
 *
 * Sem vínculo, sem pendência e sem a marca de orçamento, a seção diz a regra ("é cadastrada pelo
 * primeiro orçamento, quando tem CNPJ ou CPF"), e não uma promessa: o SPA não sabe se a ida já foi
 * pedida (a liberação não está na resposta). Com a marca, a regra some — o aviso do topo da ficha já
 * diz o que fazer.
 *
 * Além da situação, há três selos, e eles não competem:
 *
 * * **Pendente** — o selo "Pendente de sincronização", o motivo do serviço verbatim e o botão de
 *   re-tentativa. É o estado que a história existe para tornar visível: antes dela, uma escrita
 *   que falhava no ERP não tinha testemunha nenhuma do lado de dentro.
 * * **Vinculado e em dia** — o selo "Sincronizado com o ERP" e o identificador da Pessoa lá. Ele
 *   aparece porque é a confirmação que a operação procura depois de reenviar, e porque o
 *   identificador é o que permite conferir a ficha do outro lado.
 * * **Nenhum** — a ficha sem vínculo e sem pendência: só a situação, sem selo (DESIGN.md, 22/10).
 * * **Aguardando** — o selo "Aguardando o ERP", e ele **só** existe logo depois de um reenvio numa
 *   ficha que nunca subiu: a pendência foi limpa e o vínculo ainda não chegou. Sem ele a seção
 *   inteira desaparecia debaixo do dedo de quem acabou de clicar, sem confirmação e sem erro — os
 *   dois sinais ficam falsos nessa janela, que é justamente a pendência mais comum enquanto não há
 *   chave da Silcom.
 *
 * **O motivo vai à tela verbatim.** Ele vem de `SincronizacaoErpFalhou.motivo`, montado pelo
 * serviço com o passo que caiu e o status — nunca token, corpo ou cabeçalho. Reescrevê-lo aqui
 * seria uma segunda redação da mesma frase, e a que a pessoa lê seria a que ninguém revisou.
 *
 * **O botão não escreve no ERP.** Ele republica o evento; a escrita acontece no trabalhador,
 * depois. Por isso o texto de sucesso diz "reenviada" e não "sincronizada", e por isso o selo pode
 * **voltar** na passada seguinte se a nova tentativa falhar de novo — o que é a verdade, e não um
 * defeito da tela.
 *
 * **A recusa é do serviço, e vai verbatim também.** O `409` de "não está mais pendente" é o caso
 * comum: o trabalhador venceu a corrida com o clique. Como a mutação relê a ficha nos **dois**
 * desfechos (`onSettled`), o erro aparece uma vez e a seção já se redesenha no estado novo.
 *
 * **Paridade de celular (NFR-1).** Não há função exclusiva do desktop e não há árvore alternativa:
 * o selo, o motivo e o botão existem nas duas larguras, e o que muda é só como eles se empilham.
 * Sem `@media`.
 */

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useSessao } from '@/modulos/identidade/sessao'
import { useEmpresa, useReenviarSincronizacao } from './api'
import { nomeExibidoDaEmpresa } from './nome-da-empresa'
import './sincronizacao-com-o-erp.css'

/** `id` do título — é ele que nomeia a `<section>` para o leitor de tela. */
const ID_DO_TITULO = 'sincronizacao-erp-titulo'

export const TITULO = 'Sincronização com o ERP'

export const SELO_PENDENTE = 'Pendente de sincronização'
export const SELO_EM_DIA = 'Sincronizado com o ERP'

/**
 * O terceiro estado, e ele só existe **depois de um reenvio**: a pendência foi limpa e o vínculo
 * ainda não chegou.
 *
 * Ele é o estado real de quem clicou em "Reenviar" numa ficha que nunca subiu ao ERP — a pendência
 * mais comum enquanto não há chave da Silcom. O serviço republica o evento e responde na hora; quem
 * escreve no Carga Máquina é o trabalhador, depois. Dizer "Sincronizado" aqui afirmaria um desfecho
 * que ninguém observou, e desmontar a seção (o que esta tela fazia) sumiria com o cartão debaixo do
 * dedo de quem acabou de clicar, sem confirmação nem erro.
 */
export const SELO_AGUARDANDO = 'Aguardando o ERP'

export const REENVIAR = 'Reenviar ao ERP'
const REENVIANDO = 'Reenviando…'
const REENVIADA = 'Sincronização reenviada. O envio acontece em segundo plano.'

const AJUDA_PENDENTE =
  'Os dados desta empresa não chegaram ao Carga Máquina na última tentativa. Nada se perdeu no ' +
  'CRM: reenvie quando quiser, ou espere a próxima alteração da ficha, que tenta de novo sozinha.'

/** A situação (13.11c): segue só o `erp_pessoa_id`. Texto da ficha, sem selo. */
export const SITUACAO_CADASTRADA = 'Cadastrada no Carga Máquina.'
export const SITUACAO_AINDA_NAO = 'Ainda não está no Carga Máquina.'

/**
 * A regra, na ficha sem vínculo, sem pendência e sem a marca de orçamento. É regra, e não promessa:
 * a ficha liberada que espera o vínculo cai aqui também, e o SPA não a distingue.
 */
export const AJUDA_SEM_ENVIO =
  'A empresa é cadastrada no Carga Máquina pelo primeiro orçamento, quando tem CNPJ ou CPF.'

const AJUDA_AGUARDANDO =
  'O envio foi recolocado na fila e acontece em segundo plano. Se o Carga Máquina recusar de ' +
  'novo, o selo de pendência volta com o motivo da vez.'

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

export interface PropriedadesDaSincronizacao {
  /** A Empresa dona do estado. A seção só existe na tela de edição, então ela sempre existe. */
  empresaId: string
}

export function SincronizacaoComOErp({ empresaId }: PropriedadesDaSincronizacao) {
  // A **mesma** chave que o formulário da ficha já lê: nenhuma consulta nova sai da tela por causa
  // desta seção, e a resposta da mutação atualiza as duas de uma vez.
  const consulta = useEmpresa(empresaId)
  const reenvio = useReenviarSincronizacao(empresaId)
  const { data: sessao } = useSessao()

  const ficha = consulta.data
  const pendente = ficha?.pendente_de_sincronizacao === true
  const podeEditar = sessao !== undefined && 'editar_empresa' in sessao.permissoes
  const vinculada = ficha?.erp_pessoa_id !== null && ficha?.erp_pessoa_id !== undefined

  // O reenvio **em curso ou já assentado** segura o terceiro selo. Numa ficha sem `erp_pessoa_id`
  // — a pendência mais comum enquanto não há chave do ERP — o `POST` limpa a pendência e o vínculo
  // ainda não existe: sem esta condição, a seção voltaria à ficha "nova" debaixo do dedo de quem
  // acabou de clicar, sem confirmação. O estado é da tela: saindo e voltando, ele não sobrevive.
  const reenvioNaTela = reenvio.isPending || reenvio.isSuccess || reenvio.isError

  // A ficha que ainda carrega, ou que não chegou: a seção não é montada. O erro da consulta não é
  // desenhado aqui — o formulário da ficha, logo acima, já mostra o `404`, e uma segunda cópia da
  // mesma frase na mesma tela seria ruído.
  if (ficha === undefined) {
    return null
  }

  // Quatro estados de selo, e a ordem é a da urgência: pendente pede ação, vinculada confirma,
  // aguardando só existe logo depois de um reenvio, e nenhum é a ficha que nunca tentou subir.
  const situacao = pendente
    ? 'pendente'
    : vinculada
      ? 'em-dia'
      : reenvioNaTela
        ? 'aguardando'
        : 'nenhum'
  const selo =
    situacao === 'pendente'
      ? SELO_PENDENTE
      : situacao === 'em-dia'
        ? SELO_EM_DIA
        : situacao === 'aguardando'
          ? SELO_AGUARDANDO
          : null
  const ajuda =
    situacao === 'pendente'
      ? AJUDA_PENDENTE
      : situacao === 'aguardando'
        ? AJUDA_AGUARDANDO
        : situacao === 'nenhum' && !ficha.recebeu_orcamento_sem_documento
          ? AJUDA_SEM_ENVIO
          : null

  return (
    <section className="sincronizacao-erp" aria-labelledby={ID_DO_TITULO}>
      <div className="sincronizacao-erp__cartao" data-pendente={pendente ? 'sim' : 'nao'}>
        <div className="sincronizacao-erp__cabecalho">
          <h2 className="sincronizacao-erp__titulo" id={ID_DO_TITULO}>
            {TITULO}
          </h2>
          {selo === null ? null : (
            <span className="sincronizacao-erp__selo" data-situacao={situacao}>
              {selo}
            </span>
          )}
        </div>

        <div className="sincronizacao-erp__corpo">
          {/* A situação no Carga Máquina (13.11c): texto, sem selo, e só pelo vínculo. */}
          <p className="sincronizacao-erp__situacao">
            {vinculada ? SITUACAO_CADASTRADA : SITUACAO_AINDA_NAO}
          </p>
          {ajuda === null ? null : <p className="sincronizacao-erp__ajuda">{ajuda}</p>}

          {/* O motivo do serviço, verbatim. `role="status"` e não `alert`: a informação já está
              na tela quando ela abre, e interromper o leitor de tela por um estado que não acabou
              de acontecer atrapalharia em vez de ajudar. */}
          {pendente && ficha.motivo_da_pendencia !== null ? (
            <p className="sincronizacao-erp__motivo" role="status">
              {ficha.motivo_da_pendencia}
            </p>
          ) : null}

          {/* O identificador da Pessoa no ERP: é por ele que se confere a ficha do outro lado. Ele
              não é dinheiro e não é segredo — é o `id` público de uma Pessoa no Carga Máquina. */}
          {vinculada ? (
            <p className="sincronizacao-erp__vinculo">
              Pessoa no ERP: <code>{ficha.erp_pessoa_id}</code>
            </p>
          ) : null}

          {/* 13.14b (DW-487): "Reenviar" só a quem é dono (`quem_le_e_o_dono`) e tem
              `editar_empresa` — a guarda da rota; o selo e o motivo ficam para todos. */}
          {pendente && ficha.quem_le_e_o_dono && podeEditar ? (
            <div className="sincronizacao-erp__acoes">
              <button
                type="button"
                className="sincronizacao-erp__acao"
                /* O nome acessível carrega o nome da Empresa (13.7: o exibido): quem navega por leitor
                   de tela chega ao botão sem o contexto visual da tela em volta. É o molde de
                   `Tarefas.tsx`. */
                aria-label={`${REENVIAR}: ${nomeExibidoDaEmpresa(ficha)}`}
                disabled={reenvio.isPending}
                onClick={() => {
                  reenvio.mutate()
                }}
              >
                {reenvio.isPending ? REENVIANDO : REENVIAR}
              </button>
            </div>
          ) : null}

          {reenvio.isError ? (
            <p className="sincronizacao-erp__erro" role="alert">
              {mensagemDe(reenvio.error)}
            </p>
          ) : null}
          {/* "Reenviada", e não "sincronizada": o serviço republicou o evento, e quem escreve no
              Carga Máquina é o trabalhador, depois. Dizer "sincronizada" prometeria um desfecho
              que esta requisição não observou. */}
          {reenvio.isSuccess && !pendente ? (
            <p className="sincronizacao-erp__sucesso" role="status">
              {REENVIADA}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  )
}
